import classNames from 'classnames';
import { useId } from 'react';
import Typography from '@/components/atoms/typography/typography';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './progress-bar.module.scss';

export interface ProgressBarProps {
  /** The class name to apply */
  className?: string;
  /** Current step */
  currentStep: number;
  /** Total amount of steps */
  totalSteps: number;
}

const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};

const ProgressBar = ({
  className,
  currentStep,
  totalSteps,
}: ProgressBarProps) => {
  const { t } = useI18n();

  const labelId = useId();
  const normalizedTotalSteps = Math.max(totalSteps, 1);
  const normalizedCurrentStep = clamp(currentStep, 0, normalizedTotalSteps);
  const currentStepNumber = totalSteps === 0 ? 0 : normalizedCurrentStep;
  const progressPercentage = (currentStepNumber / normalizedTotalSteps) * 100;
  const progressLabel = t('items.profile.onboardingPage.progressBarStep', {
    currentStepNumber,
    totalSteps,
  });

  return (
    <div className={classNames(styles.component, className)}>
      <div
        aria-labelledby={labelId}
        aria-valuemax={normalizedTotalSteps}
        aria-valuemin={0}
        aria-valuenow={currentStepNumber}
        aria-valuetext={progressLabel}
        className={styles.track}
        role="progressbar"
      >
        <div
          className={styles.fill}
          style={{ width: `${progressPercentage}%` }}
        />
      </div>

      <Typography className={styles.label} id={labelId} tagStyle="bodySmall">
        {progressLabel}
      </Typography>
    </div>
  );
};

ProgressBar.displayName = 'ProgressBar';

export default ProgressBar;
